// Spec §75: subscription status + restore. The web client is read-mostly
// — purchases are store-driven (iOS / Android) per spec #73. The web
// page shows the current plan, lets the user trigger a restore against
// the server, and links to the mobile paywall for upgrades.
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { subscriptionApi } from '@/api/subscriptionApi';
import { ApiException } from '@/api/errors';
import { formatDate } from '@/utils/format';
import type { SubscriptionView } from '@/api/types';

export function SubscriptionScreen() {
  const { getToken } = useAuth();
  const [view, setView] = useState<SubscriptionView | null>(null);
  const [busy, setBusy] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (force = false) => {
      if (force) setBusy(true);
      setError(null);
      try {
        const data = await subscriptionApi.getCurrent(getToken);
        setView(data);
      } catch (e) {
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not load your subscription.');
      } finally {
        setBusy(false);
      }
    },
    [getToken],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRestore() {
    setRestoring(true);
    setError(null);
    try {
      const data = await subscriptionApi.restore(getToken);
      setView(data);
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Restore failed.');
    } finally {
      setRestoring(false);
    }
  }

  return (
    <div className="stack">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>FieldReport Pro</h2>
        {busy ? (
          <div className="empty">Loading subscription…</div>
        ) : (
          <>
            {error ? <div className="error-banner">{error}</div> : null}
            <div className="row" style={{ flexWrap: 'wrap' }}>
              <div className="field" style={{ minWidth: 160 }}>
                <span className="muted">Plan</span>
                <strong style={{ fontSize: 18 }}>{view?.plan === 'pro' ? 'Pro' : 'Free'}</strong>
              </div>
              <div className="field" style={{ minWidth: 160 }}>
                <span className="muted">Status</span>
                <strong style={{ fontSize: 18 }}>{view?.status ?? 'none'}</strong>
              </div>
              <div className="field" style={{ minWidth: 200 }}>
                <span className="muted">Renews / expires</span>
                <strong style={{ fontSize: 18 }}>{formatDate(view?.expires_at ?? null)}</strong>
              </div>
              <div className="field" style={{ minWidth: 200 }}>
                <span className="muted">Provider</span>
                <strong style={{ fontSize: 18 }}>{view?.provider ?? '—'}</strong>
              </div>
            </div>
            <p className="muted">
              FieldReport Pro removes photo upload limits and unlocks priority sync. Subscriptions
              are purchased on the mobile apps (App Store / Google Play); use{' '}
              <em>Restore purchases</em> below to sync the latest state from the store.
            </p>
            <div className="row">
              <button
                className="btn btn--primary"
                type="button"
                disabled={restoring}
                onClick={handleRestore}
              >
                {restoring ? 'Restoring…' : 'Restore purchases'}
              </button>
              <button className="btn" type="button" onClick={() => void load(true)}>
                Refresh
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
